import React, { useState, useRef, useCallback, KeyboardEvent } from 'react';
import { Send, Loader2, Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface FileAttachment {
  fileName: string;
  mimeType: string;
  data: string; // base64
  size: number;
}

interface MessageInputProps {
  onSend: (message: string, files?: FileAttachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  isLoading?: boolean;
  allowFileUploads?: boolean;
  allowedFileMimeTypes?: string | null;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const MAX_FILES = 5;

function buildAcceptString(mimeTypes: string | null | undefined): string | undefined {
  if (!mimeTypes) return undefined;
  return mimeTypes;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  maxLength = 1000,
  isLoading = false,
  allowFileUploads = false,
  allowedFileMimeTypes = null,
}) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && files.length === 0) || disabled || isLoading) return;

    onSend(trimmedMessage || '(file attachment)', files.length > 0 ? files : undefined);
    setMessage('');
    setFiles([]);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setMessage(value);

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const remaining = MAX_FILES - files.length;
    const filesToProcess = Array.from(selectedFiles).slice(0, remaining);

    for (const file of filesToProcess) {
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File ${file.name} exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`);
        continue;
      }

      try {
        const base64 = await readFileAsBase64(file);
        setFiles(prev => [...prev, {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          data: base64,
          size: file.size,
        }]);
      } catch (err) {
        console.error(`Failed to read file ${file.name}:`, err);
      }
    }

    // Reset input so selecting the same file again triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files.length]);

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const characterCount = message.length;
  const isNearLimit = characterCount > maxLength * 0.8;
  const canSend = (message.trim() || files.length > 0) && !disabled && !isLoading;

  return (
    <div className="border-t bg-background p-4">
      {/* File Preview Chips */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((file, index) => {
            const Icon = getFileIcon(file.mimeType);
            return (
              <div
                key={`${file.fileName}-${index}`}
                className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded-lg text-xs max-w-[200px] group"
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="truncate">{file.fileName}</span>
                <span className="text-muted-foreground flex-shrink-0">
                  {formatFileSize(file.size)}
                </span>
                <button
                  onClick={() => removeFile(index)}
                  className="ml-0.5 p-0.5 rounded-full hover:bg-destructive/20 transition-colors flex-shrink-0"
                  aria-label={`Remove ${file.fileName}`}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 items-end">
        {/* Attach Button */}
        {allowFileUploads && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={buildAcceptString(allowedFileMimeTypes)}
              onChange={handleFileSelect}
              className="hidden"
              aria-label="Attach files"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isLoading || files.length >= MAX_FILES}
              className={cn(
                "h-[44px] w-[44px] flex-shrink-0",
                files.length > 0 && "text-primary"
              )}
              title={files.length >= MAX_FILES ? `Max ${MAX_FILES} files` : 'Attach files'}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          </>
        )}

        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className={cn(
              'min-h-[44px] max-h-[120px] resize-none pr-12',
              'focus-visible:ring-1 focus-visible:ring-primary'
            )}
            rows={1}
          />

          {/* Character Counter */}
          {isNearLimit && (
            <div
              className={cn(
                'absolute bottom-2 right-2 text-xs',
                characterCount >= maxLength ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {characterCount}/{maxLength}
            </div>
          )}
        </div>

        <Button
          onClick={handleSend}
          disabled={!canSend}
          size="icon"
          className="h-[44px] w-[44px] flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Hint Text */}
      <div className="mt-2 text-xs text-muted-foreground">
        Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Enter</kbd> to send,{' '}
        <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Shift + Enter</kbd> for new line
      </div>
    </div>
  );
};

/** Read a File as base64 (without the data URI prefix) */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:...;base64," prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
